import {
    Component,
    OnInit,
    ChangeDetectionStrategy,
    ViewChild,
    ElementRef,
    ChangeDetectorRef,
    AfterViewInit,
    OnDestroy
} from '@angular/core';
import {BsModalRef, BsModalService} from 'ngx-bootstrap';
import {BsModalEventsEmitter} from 'src/app/modules/shared/events/bsModal.events';
import {Subscription} from 'rxjs';
import {DiaryService} from 'src/app/modules/shared/services/diary.service';
import {filter} from 'rxjs/operators';
import {LocalisationService} from 'src/app/modules/shared/services/localisation.service';
import {ExternalMealViewModel} from '../../viewModels/externalMeal.viewModel';
import {MealsComponent} from '../../meals/meals.component';
import {AppLoadingEventsEmitter} from '../../../../shared/events/loading.events';
import {ProductsService} from '../../../../shared/services/products.service';
import {ProductExternalMealViewModel} from '../../../../shared/viewModels/productExternalMeal.viewModel';
import {WeightQtyComponent} from './weight-qty.component';
import {DiaryMealComponent} from './diary-meal/diary-meal.component';
import {MealViewModel} from '../../../../shared/viewModels/meal.viewModel';
import * as moment from 'moment';

@Component({
    selector: 'app-external-meals',
    templateUrl: './external-meals.component.html',
    styleUrls: ['./external-meals.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalMealsComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly mobileWidth: number = 576;

    @ViewChild('wrapper') wrapper: ElementRef<HTMLElement>;

    date: string;
    subscriptions: Subscription[] = [];
    mealsComponent: MealsComponent;

    externalMeals: ExternalMealViewModel[] = [];

    keyword = 'title';
    data: ProductExternalMealViewModel[] = [];

    weightQtyModal: BsModalRef;
    diaryMealModal: BsModalRef;

    constructor(
        public readonly bsModalRef: BsModalRef,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
        private readonly diaryService: DiaryService,
        private readonly localisationService: LocalisationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly appIsLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly productsService: ProductsService,
    ) {
    }

    ngOnInit() {
        this.toBigger();

        this.subscriptions.push(
            this.bsModalEventsEmitter.bsModalStateChanged$.pipe(filter(bsModalRef => bsModalRef === this.bsModalRef)).subscribe(() => {
                this.init();
            })
        );
    }

    openWeightQtyModal(externalMeal: ExternalMealViewModel, index: number) {
        this.weightQtyModal = this.bsModalService.show(WeightQtyComponent, {
            backdrop: true
        });

        this.weightQtyModal.content.externalMeal = externalMeal;
        this.weightQtyModal.content.externalMealsComponent = this;
        this.weightQtyModal.content.index = index;

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.weightQtyModal);
    }

    addNewRow() {
        this.externalMeals.push({});
        this.changeDetectorRef.markForCheck();
    }

    onExternalMealSelected(externalMeal: ExternalMealViewModel, index: number) {
        this.externalMeals[index].id = externalMeal.id;
        this.externalMeals[index].title = externalMeal.title;
        this.externalMeals[index].calories = externalMeal.calories;
        this.externalMeals[index].image_type = externalMeal.image_type;
        this.externalMeals[index].image_name = externalMeal.image_name;
        this.externalMeals[index].protein = externalMeal.protein;
        this.externalMeals[index].carbs = externalMeal.carbs;
        this.externalMeals[index].fat = externalMeal.fat;
        this.externalMeals[index].by = externalMeal.by;
        this.externalMeals[index].type = externalMeal.type;
        this.externalMeals[index].qty = externalMeal.qty;
        this.externalMeals[index].weight = externalMeal.weight;
    }

    onExternalMealTitleChanged(title: string, index: number) {
        this.externalMeals[index].title = title;
    }

    changeMealWeight(newWeight: number, index: number) {
        this.data.forEach(item => {
            if (
                item.id === this.externalMeals[index].id
                && item.type === this.externalMeals[index].type
                && item.by === 'WEIGHT'
            ) {
                this.externalMeals[index].weight = newWeight;
                this.externalMeals[index].by = item.by;
                this.externalMeals[index].protein = Math.round(item.protein * (newWeight / item.weight));
                this.externalMeals[index].carbs = Math.round(item.carbs * (newWeight / item.weight));
                this.externalMeals[index].fat = Math.round(item.fat * (newWeight / item.weight));
                this.externalMeals[index].calories = Math.round(item.calories * (newWeight / item.weight));
                this.changeDetectorRef.markForCheck();

                return;
            }
        });
    }

    changeMealQty(newQty: number, index: number) {
        this.data.forEach(item => {
            if (
                item.id === this.externalMeals[index].id
                && item.type === this.externalMeals[index].type
                && item.by === 'QTY'
            ) {
                this.externalMeals[index].by = item.by;
                this.externalMeals[index].qty = newQty;
                this.externalMeals[index].protein = Math.round(item.protein * newQty);
                this.externalMeals[index].carbs = Math.round(item.carbs * newQty);
                this.externalMeals[index].fat = Math.round(item.fat * newQty);
                this.externalMeals[index].calories = Math.round(item.calories * newQty);
                this.changeDetectorRef.markForCheck();

                return;
            }
        });
    }

    onExternalMealCleared(index: number) {
        this.externalMeals[index] = {};
    }

    prompt(macro: string, index: number) {
        const value = prompt(this.localisationService.localise('enter_value'));

        if (value === null) {
            return;
        }

        // tslint:disable-next-line:radix
        this.externalMeals[index][macro] = parseInt(value);

        if (this.externalMeals[index][macro] > 1500) {
            this.externalMeals[index][macro] = 1500;
        }

        this.changeDetectorRef.markForCheck();
    }

    onSave() {
        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(true);

        const recipes = [];

        // tslint:disable-next-line:forin
        for (const index in this.externalMeals) {
            const data = this.externalMeals[index];
            data['meal_id'] = (this.externalMeals[index].diary_meal ? this.externalMeals[index].diary_meal.id : 0);

            recipes.push(data);
        }

        this.diaryService
            .updateDiaryExternalRecipes(
                {'recipes': recipes},
                this.mealsComponent.date
            )
            .subscribe(() => {
                this.clearShoppingList().subscribe(response => {
                    this.bsModalRef.hide();

                    // Recalculate macros of not eaten recipes in diary
                    this.diaryService
                        .recalculateExistingRecipes(this.mealsComponent.date)
                        .subscribe(() => {
                            this.mealsComponent.init().subscribe();
                        }, () => {
                            this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
                            this.mealsComponent.preventLoadingFromStop = false;
                        });
                });
            }, () => {
                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.mealsComponent.preventLoadingFromStop = false;
            });
    }

    private clearShoppingList() {
        const date = new Date();
        const monday = new Date(date.setDate((date.getDate() - date.getDay() + (date.getDay() === 0 ? -6 : 1))));
        const sunday = new Date(date.setDate(date.getDate() - date.getDay() + ((date.getDay() === 0 ? -6 : 1)) + 6));

        return this.diaryService.clearShoppingItems(moment(monday).format('YYYY-MM-DD'), moment(sunday).format('YYYY-MM-DD'));
    }

    removeExternalMeal(index: number) {
        this.externalMeals.splice(index, 1);

        if (this.externalMeals.length <= 0) {
            this.externalMeals.push({});
        }

        this.changeDetectorRef.markForCheck();
    }

    private init() {
        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(true);
        this.initExternalMeals();

        this.diaryService
            .getDiaryExternalRecipes(this.mealsComponent.date)
            .subscribe(externalRecipes => {

                if (externalRecipes.length === 0) {
                    this.externalMeals = [{}];
                } else {
                    this.externalMeals = externalRecipes;
                }

                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.mealsComponent.preventLoadingFromStop = false;
                this.changeDetectorRef.markForCheck();
            }, () => {
                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
                this.mealsComponent.preventLoadingFromStop = false;
            });
    }

    private initExternalMeals() {
        this.productsService
            .getExternalMeals()
            .subscribe(externalMeals => {
                this.data = externalMeals;
                this.changeDetectorRef.detectChanges();
            });
    }

    ngAfterViewInit() {
        if (window.innerWidth <= this.mobileWidth) {
            this.toFullscreen();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }

    private toFullscreen() {
        const modalDialog = this.wrapper.nativeElement.parentElement.parentElement.parentElement;
        const modalContent = this.wrapper.nativeElement.parentElement.parentElement;
        const modal = this.wrapper.nativeElement.parentElement;

        modalDialog.style.borderRadius = '0';
        modalDialog.style.display = 'flex';
        modalDialog.style.margin = '0';
        modalDialog.style.padding = '0';
        modalDialog.style.width = '100%';
        modalDialog.style.maxWidth = 'unset';
        modalDialog.style.minHeight = '100%';
        modalDialog.style.height = '100%';
        modalContent.style.borderRadius = '0';
        modal.style.display = 'block';
        modal.style.height = '100%';
    }

    private toBigger() {
        const modalDialog = this.wrapper.nativeElement.parentElement.parentElement.parentElement;

        modalDialog.style.minWidth = '50%';
    }

    openDiaryMealModal(externalMeal: ExternalMealViewModel, index: number) {
        this.diaryMealModal = this.bsModalService.show(
            DiaryMealComponent,
            {
                backdrop: true
            }
        );

        this.diaryMealModal.content.externalMeal = externalMeal;
        this.diaryMealModal.content.selectedMeal = externalMeal.diary_meal;
        this.diaryMealModal.content.externalMealsComponent = this;
        this.diaryMealModal.content.index = index;

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.diaryMealModal);
    }

    changeDiaryMeal(selectedMeal: MealViewModel, mealIndex: number) {
        this.data.forEach((item, index) => {
            if (index === mealIndex) {
                this.externalMeals[index].diary_meal = selectedMeal;

                this.changeDetectorRef.markForCheck();

                return;
            }
        });
    }

    isIOS() {
        return [
                'iPad Simulator',
                'iPhone Simulator',
                'iPod Simulator',
                'iPad',
                'iPhone',
                'iPod'
            ].includes(navigator.platform)
            // iPad on iOS 13 detection
            || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
    }
}
